import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, MapPin, ExternalLink, Share2, ArrowLeft, Compass, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import Seo from '../components/Seo';
import ArticleCard from '../components/ArticleCard';
import { Reveal, RevealGroup } from '../components/motion/Reveal';

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d)) return String(value).slice(0, 20);
  return d.toLocaleDateString('en-NG', { year: 'numeric', month: 'long', day: 'numeric' });
};

const ArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setArticle(null);
    setNotFound(false);
    fetch(`${API}/api/hub/articles/${slug}`)
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then(setArticle)
      .catch(() => setNotFound(true));
    fetch(`${API}/api/hub/articles/${slug}/related`)
      .then((r) => r.json())
      .then((d) => setRelated(d.articles || []))
      .catch(() => {});
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white pt-24">
        <div className="text-center" data-testid="article-not-found">
          <h1 className="text-4xl font-light text-stone-800 mb-4">Article not found</h1>
          <Link to="/property-hub" className="text-emerald-700 font-medium underline">Back to Property Hub</Link>
        </div>
      </div>
    );
  }

  if (!article) {
    return <div className="min-h-screen flex items-center justify-center text-stone-500 pt-24" data-testid="article-loading">Loading...</div>;
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.seo_description || article.summary,
    "image": article.featured_image,
    "datePublished": article.published_at,
    "author": { "@type": "Organization", "name": "Versatile Geopick NIG Ltd" },
    "publisher": { "@type": "Organization", "name": "Versatile Geopick NIG Ltd" }
  };

  const whatsappShare = `https://wa.me/?text=${encodeURIComponent(article.title + ' — ' + window.location.href)}`;
  const paragraphs = (article.content || '').split('\n').filter(Boolean);

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title={article.seo_title || article.title}
        description={article.seo_description || article.summary}
        jsonLd={jsonLd}
      />

      {/* Hero image */}
      <section className="relative h-[50vh] min-h-[400px] overflow-hidden pt-20">
        <img src={article.featured_image} alt={article.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-900/80 via-stone-900/40 to-stone-900/20"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 h-full flex flex-col justify-end pb-12">
          <Link to="/property-hub" data-testid="back-to-hub"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm mb-6 w-fit transition-colors">
            <ArrowLeft className="h-4 w-4" /> Property Hub
          </Link>
          <span className="w-fit px-3 py-1.5 bg-emerald-700 text-white text-xs font-medium rounded-full mb-4" data-testid="article-category">
            {article.category}
          </span>
          <h1 className="text-3xl md:text-5xl font-light text-white leading-tight" data-testid="article-title">
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 mt-5 text-sm text-stone-300">
            <span className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4" /> {formatDate(article.published_at || article.source_date)}
            </span>
            {article.locations?.length > 0 && (
              <span className="flex items-center gap-1.5">
                <MapPin className="h-4 w-4" /> {article.locations.join(', ')}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal>
            <p className="text-xl text-stone-700 leading-relaxed font-light border-l-4 border-emerald-600 pl-6 mb-10" data-testid="article-summary">
              {article.summary}
            </p>

            {paragraphs.length > 0 && (
              <div className="space-y-6 text-lg text-stone-600 leading-relaxed mb-12" data-testid="article-content">
                {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            )}

            {/* Source report box */}
            {article.source_url && (
              <div className="bg-stone-50 border border-stone-200 rounded-2xl p-6 mb-10" data-testid="source-report-box">
                <p className="text-xs font-medium text-stone-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                  <FileText className="h-4 w-4" /> Source Report
                </p>
                <p className="text-stone-700 text-sm mb-4">
                  Originally reported by <strong>{article.source_name}</strong>
                  {article.source_date ? ` on ${formatDate(article.source_date)}` : ''}.
                </p>
                <a href={article.source_url} target="_blank" rel="noopener noreferrer" data-testid="read-full-report"
                  className="inline-flex items-center gap-2 text-emerald-700 font-medium hover:text-emerald-800 transition-colors">
                  Read Full Report <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            )}

            {/* Versatile Geopick Insight */}
            {article.insight && (
              <div className="bg-emerald-700 rounded-3xl p-8 mb-10" data-testid="geopick-insight-box">
                <p className="text-emerald-200 font-medium tracking-wider uppercase text-xs mb-3 flex items-center gap-2">
                  <Compass className="h-4 w-4" /> Versatile Geopick Insight
                </p>
                <p className="text-white text-lg leading-relaxed font-light">{article.insight}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-4 mb-8">
              <a href={whatsappShare} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-share"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#25D366] text-white rounded-full font-medium hover:opacity-90 transition-opacity">
                <Share2 className="h-4 w-4" /> Share on WhatsApp
              </a>
              {article.keywords?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {article.keywords.map((k) => (
                    <span key={k} className="px-3 py-1 bg-stone-100 text-stone-600 text-xs rounded-full">{k}</span>
                  ))}
                </div>
              )}
            </div>
          </Reveal>

          {/* Subtle business CTA */}
          <Reveal>
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8 text-center" data-testid="article-business-cta">
              <h3 className="text-xl font-medium text-stone-800 mb-2">Buying Land or Property?</h3>
              <p className="text-stone-600 text-sm mb-5 max-w-xl mx-auto">
                Before purchasing, consider professional property verification, survey searches, boundary confirmation and title/documentation checks.
              </p>
              <Button className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full" asChild>
                <a href="https://wa.me/2348034494507" target="_blank" rel="noopener noreferrer">
                  Speak With Versatile Geopick
                </a>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Related articles */}
      {related.length > 0 && (
        <section className="py-20 bg-gradient-to-br from-stone-50 to-amber-50/20" data-testid="related-articles">
          <div className="max-w-7xl mx-auto px-6">
            <Reveal className="mb-12">
              <h2 className="text-3xl md:text-4xl font-light text-stone-800 tracking-tight">Related Articles</h2>
            </Reveal>
            <RevealGroup className="grid md:grid-cols-3 gap-8" stagger={0.12}>
              {related.map((a, i) => <ArticleCard key={a.id} article={a} index={i} />)}
            </RevealGroup>
          </div>
        </section>
      )}
    </div>
  );
};

export default ArticlePage;
